/**
 * Permissions Hook
 * Provides permission checking utilities based on user's subscription
 */
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { PlanFeatures } from '../types';

interface UsePermissionsReturn {
  // User status
  isAdmin: boolean;
  isSuspended: boolean;

  // Plan info
  planName: string;
  planDisplayName: string;

  // Feature checks
  hasFeature: (feature: keyof PlanFeatures) => boolean;
  hasGeographicRouting: boolean;
  hasApiAccess: boolean;
  hasPrioritySupport: boolean;

  // Limit checks
  canCreateQRCode: boolean;
  canScan: boolean;
  qrCodeLimit: number;
  scanLimit: number | null; // null = unlimited

  // Subscription status
  isFreePlan: boolean;
  isProPlan: boolean;
  isBusinessPlan: boolean;
  isPaidPlan: boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  return useMemo(() => {
    const subscription = user?.subscription;
    const plan = subscription?.plan;
    const features = plan?.features || {
      geographic_routing: false,
      api_access: false,
      priority_support: false,
      time_routing: true,
      device_routing: true,
      ab_testing: true,
    };

    const planName = plan?.name || 'free';
    const planDisplayName = plan?.display_name || 'Free';
    const qrCodeLimit = plan?.qr_code_limit || 1;
    const scanLimit = plan?.monthly_scan_limit || null;

    const hasFeature = (feature: keyof PlanFeatures): boolean => {
      return features[feature] || false;
    };

    return {
      // User status
      isAdmin: user?.is_admin || false,
      isSuspended: user?.is_suspended || false,

      // Plan info
      planName,
      planDisplayName,

      // Feature checks
      hasFeature,
      hasGeographicRouting: hasFeature('geographic_routing'),
      hasApiAccess: hasFeature('api_access'),
      hasPrioritySupport: hasFeature('priority_support'),

      // Limit checks - these are static from plan, actual usage should be checked via API
      canCreateQRCode: true, // Will be overridden by actual usage check
      canScan: true, // Will be overridden by actual usage check
      qrCodeLimit,
      scanLimit,

      // Subscription status
      isFreePlan: planName === 'free',
      isProPlan: planName === 'pro',
      isBusinessPlan: planName === 'business',
      isPaidPlan: planName !== 'free',
    };
  }, [user]);
}

/**
 * Hook to check if user can perform a specific action
 */
export function useCanPerform(action: 'create_qr' | 'use_geo_routing' | 'use_api'): boolean {
  const permissions = usePermissions();

  switch (action) {
    case 'create_qr':
      return permissions.canCreateQRCode;
    case 'use_geo_routing':
      return permissions.hasGeographicRouting;
    case 'use_api':
      return permissions.hasApiAccess;
    default:
      return false;
  }
}
